import { useQuery } from "@tanstack/react-query";
import { getOrganizationRepository } from "@/infrastructure/di";
import { queryKeys } from "../keys";
import type { Organization } from "@/domain/organization";

/** Organization data changes infrequently — use a longer stale time */
const ORG_STALE_TIME = 15 * 60 * 1000; // 15 minutes

export const useUserOrganizations = (enabled = true) => {
	return useQuery({
		queryKey: queryKeys.organization.userOrganizations(),
		queryFn: () => getOrganizationRepository().getUserOrganizations(),
		staleTime: ORG_STALE_TIME,
		enabled,
	});
};

export const useOrganizationDetails = (orgId: string, enabled = true) => {
	return useQuery({
		queryKey: queryKeys.organization.details(orgId),
		queryFn: async () => {
			const data = await getOrganizationRepository().getOrganization();
			return data as unknown as Organization;
		},
		staleTime: ORG_STALE_TIME,
		enabled: enabled && !!orgId,
	});
};

export const useOrganizationUsers = (orgId: string, enabled = true) => {
	return useQuery({
		queryKey: queryKeys.organization.users(orgId),
		queryFn: () => getOrganizationRepository().getOrganizationUsers(),
		enabled: enabled && !!orgId,
	});
};

export const useOrganizationInvitations = (orgId: string, enabled = true) => {
	return useQuery({
		queryKey: queryKeys.organization.invitations(orgId),
		queryFn: () => getOrganizationRepository().getOrganizationInvitations(),
		enabled: enabled && !!orgId,
	});
};

export const useOrganizationCertificates = (orgId: string, enabled = true) => {
	return useQuery({
		queryKey: queryKeys.organization.certificates(orgId),
		queryFn: () => getOrganizationRepository().getOrganizationCertificates(),
		enabled: enabled && !!orgId,
	});
};

export const useOrganizationTemplates = (orgId: string, enabled = true) => {
	return useQuery({
		queryKey: queryKeys.organization.templates(orgId),
		queryFn: () => getOrganizationRepository().getOrganizationTemplates(),
		enabled: enabled && !!orgId,
	});
};
